import { useEffect } from 'react';
import {
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useIngredients } from '../../hooks/useIngredients.ts';
import { FormSwitchController } from '@/components';
import { IngredientsActionsHeader } from '@/features';
import { INGREDIENTS_ROUTE } from '@/routes';
import palette from '@/theme/palette.ts';

import { styles } from './styles.ts';

export const PageIngredientsActions = ({ mode }: { mode: 'create' | 'edit' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams() || undefined;

  const { mutations, queries } = useIngredients();
  const { data } = queries.useGetIngredientById(Number(id));
  const methods = useForm({
    defaultValues: {
      plu_code: '',
      name: '',
      is_active: true,
      unit: 'kg',
    },
    mode: 'onSubmit',
  });

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = methods;

  const onMutateSuccess = () => {
    navigate(INGREDIENTS_ROUTE);
  };

  const handleSubmitForm = async () => {
    switch (mode) {
      case 'create':
        return await mutations.createMutation.mutateAsync(
          {
            ...getValues(),
          },
          { onSuccess: onMutateSuccess },
        );
      case 'edit':
        if (!data) break;
        return await mutations.updateMutation.mutateAsync(
          { ...data, ...getValues() },
          { onSuccess: onMutateSuccess },
        );
      default:
        return;
    }
  };

  useEffect(() => {
    if (id && data) {
      setValue('plu_code', data.plu_code);
      setValue('name', data.name);
      setValue('is_active', data.is_active);
      setValue('unit', data.unit);
    }
  }, [data, id, setValue]);

  return (
    <Stack
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      sx={{
        width: '100%',
        height: '100vh',
        background: palette.surface.lightest,
      }}
    >
      <IngredientsActionsHeader mode={mode} onSubmit={handleSubmitForm} />
      <Divider sx={{ width: '100%' }} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          width: '100%',
          marginLeft: '100px',
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Stack
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '280px 200px',
                placeItems: 'center',
              }}
            >
              <Controller
                name="plu_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    sx={{
                      ...styles.textField,
                      '& .MuiInputBase-input': {
                        backgroundColor: palette.background.default,
                      },
                      '& .MuiFormLabel-root': {
                        color: palette.text.info,
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                    type="string"
                    value={field.value?.toString() || ''}
                    label={t('FINANCIAL_CLOSURE.PLU')}
                    error={!!errors.plu_code}
                    helperText={errors.plu_code?.message?.toString() || ' '}
                  />
                )}
              />
              <div />
              <TextField
                {...register('name', { required: t('ERRORS.FIELD_REQUIRED') })}
                sx={{
                  ...styles.textField,
                  '& .MuiInputBase-input': {
                    backgroundColor: palette.background.default,
                  },
                  '& .MuiFormLabel-root': {
                    color: palette.text.info,
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.875rem',
                  },
                }}
                label={t('HEADERS.NAME')}
                variant="outlined"
                margin="normal"
                error={!!errors.name}
                helperText={errors.name?.message}
                InputLabelProps={{ shrink: true }}
              />

              <FormSwitchController controllerName="is_active" label={t('SWITCHER.ACTIVE')} />

              <FormControl
                sx={{
                  ...styles.textField,
                  '& .MuiInputBase-input': {
                    backgroundColor: palette.background.default,
                  },
                }}
              >
                <InputLabel>{t('HEADERS.UNIT')}</InputLabel>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label={t('HEADERS.UNIT')}>
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="l">l</MenuItem>
                      <MenuItem value="g">g</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    </Stack>
  );
};
